import { z } from 'zod';
import { ArgoConnectionKindEnum } from '../db';

// primitive data types; strings, booleans, numbers
const primitiveDataTypeSchema = z.union([
    // static data type is a data type with predefined options. For example, mode selection. 1 = Basic, 2 = Advanced, etc
    z.object({
        bitLength: z.number(),
        kind: z.literal('static'),
        dataKind: z.literal('string'),
        options: z.record(z.string()),
    }),
    z.object({
        bitLength: z.number(),
        kind: z.literal('static'),
        dataKind: z.literal('number'),
        options: z.record(z.number()),
    }),
    z.object({
        bitLength: z.number(),
        kind: z.literal('static'),
        dataKind: z.literal('boolean'),
        options: z.record(z.boolean()),
    }),
    // dynamic data type if for user input. For example volume control, any number between 0 - 100 could be valid
    z.object({
        bitLength: z.number(),
        kind: z.literal('dynamic'),
        dataKind: z.union([ z.literal('string'), z.literal('number'), z.literal('boolean') ]),
    }),
    // hybrid data type if a data type with both predefined options as well as user input. For example RPM control, any number between 1000 - 3000 is valid, but 0 is also a special option for 'OFF'
    z.object({
        bitLength: z.number(),
        kind: z.literal('hybrid'),
        options: z.record(z.union([ z.string(), z.number(), z.boolean() ])),
        dataKind: z.union([ z.literal('string'), z.literal('number'), z.literal('boolean') ]),
    }),
]);

/** keyed by process data item id, value is either a data type id or a type defined directly on the process data item */
const processDataItemsSchema = z.record(z.discriminatedUnion('kind', [
    z.object({
        /** if the data item is used in a complex object the sub index will be used to fetch specific records */
        subIndex: z.number(),
        /** used to ensure the bits are always packed in the correct order */
        bitOffset: z.number(),
        dataTypeRef: z.string(),
        displayName: z.string(),
        kind: z.literal('dataRefLookup'),
    }),
    z.object({
        /** if the data item is used in a complex object the sub index will be used to fetch specific records */
        subIndex: z.number(),
        /** used to ensure the bits are always packed in the correct order */
        bitOffset: z.number(),
        displayName: z.string(),
        kind: z.literal('selfDefined'),
        dataType: primitiveDataTypeSchema,
    }),
]));
export type ProcessDataItems = z.infer<typeof processDataItemsSchema>;

// complex data types; objects
// NOTE: I am making the assumption that IODD object types are comprised of primative types and cannot be nested
export const complexDataTypeSchema = z.object({
    bitLength: z.number(),
    kind: z.literal('complex'),
    dataKind: z.literal('object'),
    shape: processDataItemsSchema,
});

export const ioddDataTypeSchema = z.union([ primitiveDataTypeSchema, complexDataTypeSchema ]);
export type IODDDataType = z.infer<typeof ioddDataTypeSchema>;

const processDataItemSchema = z.object({
    /** key is the process data in name, value is the data type from the device */
    processDataIn: processDataItemsSchema,
    /** key is the process data out name, value is either a known, referenced data type or a data type defined directly on the process data */
    processDataOut: processDataItemsSchema,
});
export type ProcessDataItem = z.infer<typeof processDataItemSchema>;

const conditionalProcessDataSchema = z.object({
    /** variable source that specifies what each key in `options` means */
    variableSourceId: z.string(),
    kind: z.literal('conditional'),
    /** Im pretty sure the bit length will always be the same for any process data in/ out conditions respectively  */
    processDataInBitLength: z.number(),
    processDataOutBitLength: z.number(),
    options: z.record(processDataItemSchema.extend({
        /** human readable name for the operating mode. Can also be fetched from variableSourceId, but here for convenience  */
        name: z.string(),
    })),
});
const defaultProcessDataSchema = z.object({
    kind: z.literal('default'),
    default: processDataItemSchema,
    processDataInBitLength: z.number(),
    processDataOutBitLength: z.number(),
});

const processDataSchema = z.discriminatedUnion('kind', [ defaultProcessDataSchema, conditionalProcessDataSchema ]);

const variableDataItemSchema = z.discriminatedUnion('kind', [
    z.object({
        dataTypeRef: z.string(),
        displayName: z.string(),
        kind: z.literal('dataRefLookup'),
    }),
    z.object({
        displayName: z.string(),
        dataType: ioddDataTypeSchema,
        kind: z.literal('selfDefined'),
    }),
]);

const ioddVariableSchema = z.object({
    index: z.number(),
    subIndex: z.number(),
    variableData: variableDataItemSchema,
    access: z.union([ z.literal('r'), z.literal('w'), z.literal('rw') ]),
});

const ioddConfigSchema = z.object({
    deviceName: z.string(),
    /**
     * some devices have more than one operating mode. This is noted by the existance of the `Condition` tag on the `ProcessData` element.
     * A device can only be set to one mode at a time.
     * As far as I can tell the value will always be a number i.e 1 = Basic mode, 2 = Advanced mode */
    processData: processDataSchema,
    dataTypes: z.record(ioddDataTypeSchema),
    /** keyed by variable id. value is data type assoicated with variable */
    variables: z.record(ioddVariableSchema),
});

export type IODDConfig = z.infer<typeof ioddConfigSchema>;

const baseIOLinkMasterIODDDeviceChannelConfigSchema = z.object({
    kind: z.literal('iodd'),
    // nullable because an iodd does not always have to be plugged in to every channel
    config: ioddConfigSchema.nullable(),
});
export type BaseIOLinkMasterIODDDeviceChannelConfig = z.infer<typeof baseIOLinkMasterIODDDeviceChannelConfigSchema>;

const ptl110PickLightDeviceConfig = z.object({
    /** port to talk to PICK-IQ devices  */
    port: z.number(),
    kind: z.literal('ptl110PickLight'),
    deviceTimeout: z.number().default(100),
});

const baseIOLinkMasterOtherDeviceChannelConfigSchema = z.object({
    kind: z.literal('other'),
    config: z.discriminatedUnion('kind', [ ptl110PickLightDeviceConfig ]).nullable(),
});
export type BaseIOLinkMasterOtherDeviceChannelConfig = z.infer<typeof baseIOLinkMasterOtherDeviceChannelConfigSchema>;


const baseIOLinkMasterChannelDetailsSchema = z.discriminatedUnion('kind', [
    baseIOLinkMasterIODDDeviceChannelConfigSchema,
    baseIOLinkMasterOtherDeviceChannelConfigSchema,
]);

const baseIOLinkMasterConfigSchema = z.object({
    /** keyed by channel number which should always be a numeric string */
    channels: z.record(baseIOLinkMasterChannelDetailsSchema),
});

const dxmr904kIOLinkMasterConfigSchema = baseIOLinkMasterConfigSchema.extend({
    ip: z.string(),
    kind: z.literal('dxmr904k'),
    modbusTCPPort: z.number().default(502), // not configurable on ui. Can be edited via the JSON tab for development purposes. NOTE: 502 is standard modbus tcp
    channels: z.object({
        0: baseIOLinkMasterOtherDeviceChannelConfigSchema,
        1: baseIOLinkMasterIODDDeviceChannelConfigSchema,
        2: baseIOLinkMasterIODDDeviceChannelConfigSchema,
        3: baseIOLinkMasterIODDDeviceChannelConfigSchema,
        4: baseIOLinkMasterIODDDeviceChannelConfigSchema,
    }),
});
export type DXMR904KIOLinkMasterConfig = z.infer<typeof dxmr904kIOLinkMasterConfigSchema>;

export const ioLinkMasterConnectionConfigSchema = z.object({
    kind: z.literal(ArgoConnectionKindEnum.IoLinkMaster),
    details: z.discriminatedUnion('kind', [ dxmr904kIOLinkMasterConfigSchema ]),
});
export type IOLinkMasterConnectionConfig = z.infer<typeof ioLinkMasterConnectionConfigSchema>;
